import React from 'react';
import { Route, Switch } from 'react-router-dom';
import { styled } from '@this/constants/themes';

import type { AvailableRepository } from '@this/domain/available_repository';
import TripsChat from '@this/components/trips_management/trips/trips_chat/trips_chat';
import { ReceiptPage } from '@this/components/trips_management/trips/reciept';
import TripsSimpleRequestForm from '@this/components/trips_management/trips/trips_simple_request_form';
import TripReceiptForm from '@this/components/trips_management/receipts/trip_receipt_form';
import TripReceiptRevisionRequest from '@this/components/trips_management/receipts/trip_receipt_revision_request';
import { reportError } from '@this/src/lib/bugsnag';
import Trips from './trips/trips';
import TripReport from './trip_report/trip_report';
import DraftTripsNew from './draft_trips/draft_trips_new/draft_trips_new';
import DraftTripsEdit from './draft_trips/draft_trips_edit/draft_trips_edit';
import TripsForm from './trips/trips_form';
import TripDetail from './trip_detail/trip_detail';
import TripsDetailForm from './trips/trips_detail_form';
import TripsConfirm from './trips/trips_confirm/trips_confirm';
import TripAiMessagesPage from './trips/trip_ai_messages/TripAiMessagesPage';
import TripAiMessagesNewPage from './trips/trip_ai_messages_new/TripAiMessagesNewPage';
import type User from '../../domain/user/user';
import { TripReportNew } from './trip_report/trip_report_new';
import { TripReportShow } from './trip_report/trip_report_show';
import { TripReportEdit } from './trip_report/trip_report_edit';
import TripReportExpenses from './trip_report/trip_report_expenses';
import TripReportApproval from './trip_report/trip_report_approval';
import TripReportApprovals from './trip_report/trip_report_approvals';

export const TripsManagementPaths = [
  '/trips',
  '/trips/new',
  '/trips/:id/edit',
  '/trips/:id/confirm',
  '/trips/:id/detail',
  '/trips/:id',
  '/trips/:id/messages',
  '/trips/:id/ai_messages',
  '/trips/:id/receipts',
  '/trip_report',
  '/trip_report/expenses',
  '/trip_report/approvals',
  '/trip_report/approvals/:id',
  '/trip_report/:id',
  '/trip_receipts',
  '/trip_receipts/new',
  '/trip_receipts/:id/revision_request',
  '/trip_receipts/:id',
  '/draft_trips/new',
  '/draft_trips/:id/edit'
];

interface Props {
  user: User | null;
  serviceId: number;
  availableRepos: AvailableRepository[];
  availableOptions: string[];
}

const TripsManagement: React.FC<Props> = props => {
  try {
    const { user, serviceId, availableRepos, availableOptions } = props;
    const allowanceAvailable = availableOptions.includes('daily_allowance');
    return (
      <Switch>
        <Route path="/trips/:id/receipts" component={ReceiptPage} />
        <Route
          exact
          path={['/trips/new', '/trips/:id/edit']}
          render={routeProps =>
            user ? <TripsForm {...{ user, serviceId, availableRepos }} {...routeProps} /> : <Spacer />
          }
        />
        <Route
          exact
          path="/trips/simple_request"
          render={routeProps =>
            user && user?.organization?.show_exic_seisan_trips ? (
              <TripsSimpleRequestForm {...{ user, serviceId, availableRepos }} {...routeProps} />
            ) : (
              <Spacer />
            )
          }
        />
        <Route
          exact
          path={['/trips/:id/detail']}
          render={routeProps =>
            user ? <TripsDetailForm {...{ user, serviceId, availableRepos }} {...routeProps} /> : <Spacer />
          }
        />
        <Route
          exact
          path={['/trips/confirm', '/trips/:id/confirm']}
          render={routeProps => <TripsConfirm {...routeProps} serviceId={serviceId} />}
        />
        <Route
          exact
          path="/trips/:id/messages"
          render={routeProps => <TripsChat {...routeProps} serviceId={serviceId} />}
        />
        <Route
          exact
          path="/trips/:id/ai_messages"
          render={routeProps => (user ? <TripAiMessagesPage {...routeProps} user={user} /> : <Spacer />)}
        />
        <Route exact path="/trips/ai_messages/new" component={TripAiMessagesNewPage} />
        <Route
          path="/trips/:id"
          render={routeProps => (
            <TripDetail {...{ user, serviceId, availableRepos, availableOptions }} {...routeProps} />
          )}
        />
        <Route
          exact
          path="/trips"
          render={routeProps => (
            <Trips {...{ user, serviceId, availableRepos, availableOptions }} {...routeProps} selectedKey="trip" />
          )}
        />

        <Route
          exact
          path="/trip_receipts"
          render={props => (
            <Trips {...{ user, serviceId, availableRepos, availableOptions }} {...props} selectedKey="receipt" />
          )}
        />
        <Route
          exact
          path="/trip_receipts/new"
          render={props => (
            <TripReceiptForm
              {...props}
              user={user}
              tripReceiptAvailable={(availableOptions || []).includes('receipt')}
            />
          )}
        />
        <Route exact path="/trip_receipts/:tripId/revision_request" component={TripReceiptRevisionRequest} />
        <Route
          exact
          path="/trip_report"
          render={routeProps => (
            <TripReport
              {...routeProps}
              serviceId={serviceId}
              allowanceAvailable={allowanceAvailable}
              availableOptions={availableOptions}
            />
          )}
        />
        <Route
          exact
          path="/trip_report/expenses"
          render={routeProps => (
            <TripReportExpenses
              {...routeProps}
              serviceId={serviceId}
              allowanceAvailable={allowanceAvailable}
              availableOptions={availableOptions}
            />
          )}
        />
        <Route
          exact
          path="/trip_report/approvals"
          render={routeProps => (
            <TripReportApprovals {...routeProps} serviceId={serviceId} allowanceAvailable={allowanceAvailable} />
          )}
        />
        <Route
          exact
          path="/trip_report/approvals/:id"
          render={routeProps => (
            <TripReportApproval {...routeProps} serviceId={serviceId} allowanceAvailable={allowanceAvailable} />
          )}
        />
        <Route
          exact
          path="/trip_report/new"
          render={routeProps => (
            <TripReportNew {...routeProps} serviceId={serviceId} allowanceAvailable={allowanceAvailable} />
          )}
        />
        <Route
          exact
          path="/trip_report/:id"
          render={routeProps => (
            <TripReportShow {...routeProps} serviceId={serviceId} allowanceAvailable={allowanceAvailable} />
          )}
        />
        <Route
          exact
          path="/trip_report/:id/edit"
          render={routeProps => (
            <TripReportEdit {...routeProps} serviceId={serviceId} allowanceAvailable={allowanceAvailable} />
          )}
        />
        <Route path="/draft_trips/:id/edit" component={DraftTripsEdit} />
        <Route exact path="/draft_trips/new" component={DraftTripsNew} />
      </Switch>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const Spacer = styled.div`
  flex-grow: 9999;
  background: white;
`;

export default TripsManagement;
